import { Box, Tooltip, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../../../../global/theme/tokens';

const SIZE = 130;
const STROKE = 16;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

// Donut of the whole project with the same segments as the work package bars.
const ProgressDonut = ({ stats, percent }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const { total, done, open, overdue } = stats;

  const segments = [
    { label: t('Done'), color: colors.success, value: done },
    { label: t('Open'), color: colors.info, value: Math.max(open - overdue, 0) },
    { label: t('Overdue'), color: colors.error, value: overdue },
  ];

  // each segment starts where the previous one ended
  let offset = 0;

  return (
    <Box className="relative shrink-0" sx={{ width: SIZE, height: SIZE }}>
      <svg width={SIZE} height={SIZE} style={{ transform: 'rotate(-90deg)' }}>
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          fill="none"
          stroke={colors.bgInput}
          strokeWidth={STROKE}
        />
        {/* hovering a part tells what it stands for */}
        {segments.map((segment, index) => {
          const length = total ? (segment.value / total) * CIRCUMFERENCE : 0;
          const start = offset;
          offset += length;
          const share = total ? Math.round((segment.value / total) * 100) : 0;

          return (
            <Tooltip
              key={index}
              // a ring spans the whole svg, so the tooltip follows the cursor
              // instead of sticking to the bottom of that box
              followCursor
              title={`${segment.label}: ${segment.value} (${share}%)`}
            >
              <circle
                cx={SIZE / 2}
                cy={SIZE / 2}
                r={RADIUS}
                fill="none"
                stroke={segment.color}
                strokeWidth={STROKE}
                strokeDasharray={`${length} ${CIRCUMFERENCE - length}`}
                strokeDashoffset={-start}
              />
            </Tooltip>
          );
        })}
      </svg>

      {/* the label lies over the whole svg, so it must not eat the hover */}
      <Box
        className="absolute inset-0 flex flex-col items-center justify-center"
        sx={{ pointerEvents: 'none' }}
      >
        <Typography fontSize="20px" fontWeight={600}>
          {`${percent}%`}
        </Typography>
        <Typography fontSize="10px" className="opacity-60">
          {t('completed')}
        </Typography>
      </Box>
    </Box>
  );
};

export default ProgressDonut;
